import { useEffect, useState } from 'react';
import type { AvoidSchedule, BuildSchedule, HabitCategory, HabitPlan, HabitView, ObjectiveSummaryView } from '@contracts';
import { useDomainApi, useDomainVersion } from '@ui/shared/domain/DomainApiContext';
import { ChevronRight, TrendingUp, Shield } from '@ui/shared/icons';
import { SkeletonBlock } from '@ui/shared/primitives/Skeleton';
import { HABIT_CATEGORIES, habitCategoryMeta } from '@ui/shared/habits/habitCategory';
import { Field, PrimaryButton, SecondaryButton, inputStyle } from '@ui/shared/primitives/FormControls';
import { FooterTextButton, LifecycleConfirmDialog, LifecycleFooterShell } from '@ui/shared/primitives/LifecycleFooter';
import { ObjectivePickerField } from '@ui/phone/habits/ObjectivePickerField';

/**
 * Desktop-only Habit create/edit screen — same posture as
 * `ObjectiveFormScreen.desktop.tsx` relative to the phone objective forms: a
 * genuinely separate desktop destination, reached from `HabitsScreenDesktop`,
 * NOT a restyle of the phone `HabitFormScreen.tsx` (which stays completely
 * untouched). Two-column layout matching the Objective form screen's
 * structure (top bar, left-column fields, 380px sticky right sidebar).
 *
 * UNLIKE the Objective form screen, a habit has no draft/autosave lifecycle
 * at all — confirmed against `HabitFormScreen.tsx` (phone) and the
 * `DomainApi` contract: `createHabit`/`editHabit` are simple, immediate
 * commands, no `ObjectiveDraft.initialStatus`-style concept exists for
 * habits. This screen intentionally keeps that same simple model: fields
 * are local state only until Create/Save is pressed (create path) or commit
 * immediately per-field via `editHabit`/`adjustTargetManually` (edit path,
 * matching the phone form's own "no save button for edits" behavior) — no
 * autosave timer, no draft entity, no autosave-status pill.
 */
export function HabitFormScreenDesktop({
  habitId,
  onDone,
  onCancel,
}: {
  /** Null = create flow; a HabitId string = edit flow. */
  readonly habitId: string | null;
  readonly onDone: () => void;
  readonly onCancel: () => void;
}) {
  const api = useDomainApi();
  const version = useDomainVersion();
  const isEdit = habitId !== null;

  const [loaded, setLoaded] = useState(!isEdit);
  const [existing, setExisting] = useState<HabitView | null>(null);
  const [activeObjectives, setActiveObjectives] = useState<readonly ObjectiveSummaryView[]>([]);
  // Mirrors the phone form's habit-form.md §2 "already-linked-but-now-
  // archived" fallback: a separate includeArchived:true list used ONLY to
  // resolve the closed field's display label, never as picker options.
  const [allObjectivesForLabel, setAllObjectivesForLabel] = useState<readonly ObjectiveSummaryView[]>([]);

  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [category, setCategory] = useState<HabitCategory>('health');
  const [habitKind, setHabitKind] = useState<'build' | 'avoid' | null>(null);
  const [buildFrequency, setBuildFrequency] = useState<'daily' | 'times_per_week'>('daily');
  const [buildTarget, setBuildTarget] = useState(3);
  const [avoidLimit, setAvoidLimit] = useState(2);
  const [objectiveId, setObjectiveId] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void api.listObjectives().then(setActiveObjectives);
    void api.listObjectives({ includeArchived: true }).then(setAllObjectivesForLabel);
  }, [api, version]);

  useEffect(() => {
    if (!isEdit || !habitId) return;
    let cancelled = false;
    void api.listHabits({ includeArchived: true }).then((list) => {
      if (cancelled) return;
      const found = list.find((h) => h.habitId === habitId) ?? null;
      setExisting(found);
      if (found) {
        setName(found.name);
        setCategory(found.category);
        setHabitKind(found.plan.habitKind);
        setObjectiveId(found.objectiveId);
        if (found.plan.habitKind === 'build') {
          setBuildFrequency(found.plan.schedule.kind);
          if (found.plan.schedule.kind === 'times_per_week') {
            setBuildTarget(found.plan.schedule.targetPerWeek);
          }
        } else {
          setAvoidLimit(found.plan.schedule.limitPerWeek);
        }
      }
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, [api, habitId, isEdit, version]);

  if (isEdit && !loaded) {
    return (
      <main aria-label="Habit form" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}>
        <SkeletonBlock height={20} width={160} />
        <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <SkeletonBlock height={120} />
            <SkeletonBlock height={200} />
          </div>
          <SkeletonBlock height={280} />
        </div>
      </main>
    );
  }

  if (isEdit && !existing) {
    return (
      <main aria-label="Habit form" style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) 0', maxWidth: '1600px', margin: '0 auto' }}>
        <Breadcrumb title={null} onNavigateBack={onCancel} />
        <p style={{ marginTop: 'var(--space-4)', fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)' }}>Habit not found.</p>
      </main>
    );
  }

  const canSubmit = name.trim().length > 0 && habitKind !== null && !submitting;

  function planFromForm(): HabitPlan {
    if (habitKind === 'avoid') {
      const schedule: AvoidSchedule = { kind: 'weekly_limit', limitPerWeek: avoidLimit };
      return { habitKind: 'avoid', schedule };
    }
    const schedule: BuildSchedule =
      buildFrequency === 'daily' ? { kind: 'daily' } : { kind: 'times_per_week', targetPerWeek: buildTarget };
    return { habitKind: 'build', schedule };
  }

  async function submit() {
    setNameTouched(true);
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      if (isEdit && habitId) {
        await api.editHabit(habitId as never, { name: name.trim(), category, objectiveId: objectiveId as never });
        // Frequency changes on an existing habit are a manual target
        // adjustment (HB-4), matching the phone form's exact rule.
        if (existing) {
          const nextPlan = planFromForm();
          if (nextPlan.habitKind === existing.plan.habitKind && JSON.stringify(nextPlan) !== JSON.stringify(existing.plan)) {
            if (nextPlan.habitKind === 'build' && existing.plan.habitKind === 'build') {
              await api.adjustTargetManually(habitId as never, {
                habitKind: 'build',
                previous: existing.plan.schedule,
                next: nextPlan.schedule,
              });
            } else if (nextPlan.habitKind === 'avoid' && existing.plan.habitKind === 'avoid') {
              await api.adjustTargetManually(habitId as never, {
                habitKind: 'avoid',
                previous: existing.plan.schedule,
                next: nextPlan.schedule,
              });
            }
          }
        }
      } else {
        await api.createHabit({
          name: name.trim(),
          category,
          plan: planFromForm(),
          objectiveId: objectiveId as never,
        });
      }
      onDone();
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main
      aria-label={isEdit ? `Edit habit: ${existing?.name ?? ''}` : 'New habit'}
      style={{ padding: 'var(--space-8) var(--layout-page-margin-desktop) var(--space-16)', maxWidth: '1600px', margin: '0 auto' }}
    >
      <TopBar
        title={isEdit ? (existing?.name ?? null) : null}
        onNavigateBack={onCancel}
        onCancel={onCancel}
        onSubmit={() => void submit()}
        submitLabel={isEdit ? 'Save changes' : 'Create habit'}
        canSubmit={canSubmit}
      />

      <div style={{ marginTop: 'var(--space-6)', display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 380px', gap: 'var(--space-6)', alignItems: 'flex-start' }}>
        <div style={{ minWidth: 0 }}>
          <SectionHeader>01 · Definition</SectionHeader>
          <Field label="Name">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => setNameTouched(true)}
              placeholder="e.g. Gym"
              style={inputStyle}
            />
          </Field>
          {nameTouched && name.trim().length === 0 && (
            <p role="status" style={{ color: 'var(--text-secondary)', fontSize: 'var(--type-caption-size)', marginTop: 'var(--space-2)' }}>
              Give your habit a name to continue.
            </p>
          )}

          <div style={{ marginTop: 'var(--space-4)' }}>
            <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              Category
            </span>
            <div role="radiogroup" aria-label="Category" style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
              {HABIT_CATEGORIES.map((cat) => {
                const meta = habitCategoryMeta(cat);
                const selected = cat === category;
                return (
                  <button
                    key={cat}
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setCategory(cat)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      padding: '0 var(--space-3)',
                      minHeight: 'var(--touch-target-min)',
                      borderRadius: 'var(--radius-md)',
                      border: selected ? `1px solid var(--accent-${meta.tokenSuffix})` : '1px solid var(--border-strong)',
                      background: selected ? `color-mix(in srgb, var(--accent-${meta.tokenSuffix}-vivid) 14%, var(--surface-raised))` : 'transparent',
                      color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
                      fontSize: 'var(--type-body-size)',
                      fontWeight: 500,
                    }}
                  >
                    <meta.Icon size={16} color={`var(--accent-${meta.tokenSuffix})`} />
                    {meta.label}
                  </button>
                );
              })}
            </div>
          </div>

          <SectionHeader>02 · Shape</SectionHeader>
          <div>
            <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
              Type
            </span>
            <div role="radiogroup" aria-label="Type" style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <TypeOptionCard
                icon={<TrendingUp size={20} aria-hidden="true" />}
                title="Build"
                description="Something you want to do. Exercise, read, practice."
                frequency="Daily, or at least N times a week."
                selected={habitKind === 'build'}
                disabled={isEdit}
                onSelect={() => setHabitKind('build')}
              />
              <TypeOptionCard
                icon={<Shield size={20} aria-hidden="true" />}
                title="Avoid"
                description="Something you want to limit. Alcohol, sweets, doom-scrolling."
                frequency="A weekly allowance: at most N times."
                selected={habitKind === 'avoid'}
                disabled={isEdit}
                onSelect={() => setHabitKind('avoid')}
              />
            </div>
            <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
              Rule of thumb: if it&apos;s about doing a good thing, build it — &quot;exercise 3× a week&quot;, not &quot;avoid skipping
              exercise&quot;.
            </p>
          </div>

          {habitKind === 'build' && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
                Frequency
              </span>
              <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                <FrequencySegment
                  label="Daily"
                  selected={buildFrequency === 'daily'}
                  onSelect={() => setBuildFrequency('daily')}
                />
                <FrequencySegment
                  label="N× a week"
                  selected={buildFrequency === 'times_per_week'}
                  onSelect={() => setBuildFrequency('times_per_week')}
                />
                {buildFrequency === 'times_per_week' && (
                  <NumberStepper value={buildTarget} min={1} max={7} onChange={setBuildTarget} />
                )}
              </div>
            </div>
          )}

          {habitKind === 'avoid' && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-2)' }}>
                At most, per week
              </span>
              <NumberStepper value={avoidLimit} min={0} max={14} onChange={setAvoidLimit} />
              <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: 'var(--space-2)' }}>
                {avoidLimit === 0 ? 'Zero — aiming for none this week.' : `Up to ${avoidLimit} times allowed this week.`}
              </p>
            </div>
          )}

          <SectionHeader>03 · Objective</SectionHeader>
          <div>
            <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
              Linked objective
            </span>
            <ObjectivePickerField
              value={objectiveId}
              activeObjectives={activeObjectives}
              linkedTitleFallback={
                allObjectivesForLabel.find((o) => o.objectiveId === objectiveId)?.title ?? null
              }
              onChange={setObjectiveId}
            />
          </div>
        </div>

        <aside style={{ position: 'sticky', top: 'var(--space-8)', display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <ThisWeekPreview habitKind={habitKind} buildFrequency={buildFrequency} buildTarget={buildTarget} avoidLimit={avoidLimit} />

          {isEdit && existing && habitId && (
            <LifecyclePanel habitId={habitId} state={existing.state} name={existing.name} onChanged={onDone} />
          )}
        </aside>
      </div>
    </main>
  );
}

function ThisWeekPreview({
  habitKind,
  buildFrequency,
  buildTarget,
  avoidLimit,
}: {
  readonly habitKind: 'build' | 'avoid' | null;
  readonly buildFrequency: 'daily' | 'times_per_week';
  readonly buildTarget: number;
  readonly avoidLimit: number;
}) {
  // Plain-language restatement of the selected type/frequency only — no new
  // math, no domain call. Mirrors the "no domain math in components" rule:
  // this is a direct echo of local form state, same as the phone form's own
  // zero-allowance helper copy just brought up into the sidebar.
  const summary =
    habitKind === null
      ? 'Choose a type to see a preview here.'
      : habitKind === 'build'
        ? buildFrequency === 'daily'
          ? 'Build · Daily'
          : `Build · ${buildTarget}× a week`
        : avoidLimit === 0
          ? 'Avoid · aiming for none this week'
          : `Avoid · at most ${avoidLimit} per week`;

  return (
    <div
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--type-label-size)',
          fontWeight: 'var(--type-label-weight)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        This week
      </h2>
      <p style={{ marginTop: 'var(--space-3)', fontSize: 'var(--type-heading-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
        {summary}
      </p>
    </div>
  );
}

/**
 * Edit-path-only lifecycle panel — same actions/copy as the phone form's
 * `HabitLifecycleFooter`, reusing `LifecycleFooterShell`/`FooterTextButton`/
 * `LifecycleConfirmDialog` directly rather than duplicating that chrome.
 * Laid out as its own sidebar card instead of the phone form's bottom
 * footer, since the desktop form has no natural document "bottom" left over
 * once the right column exists.
 */
function LifecyclePanel({
  habitId,
  state,
  name,
  onChanged,
}: {
  readonly habitId: string;
  readonly state: HabitView['state'];
  readonly name: string;
  readonly onChanged: () => void;
}) {
  const api = useDomainApi();
  const [confirming, setConfirming] = useState<'pause' | 'archive' | null>(null);

  return (
    <div
      style={{
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-xl)',
        background: 'var(--surface-raised)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: 'var(--type-label-size)',
          fontWeight: 'var(--type-label-weight)',
          letterSpacing: 'var(--type-label-tracking)',
          textTransform: 'uppercase',
          color: 'var(--text-muted)',
        }}
      >
        Lifecycle
      </h2>
      <LifecycleFooterShell>
        {state === 'paused' && (
          <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginBottom: 'var(--space-3)' }}>
            Paused.
          </p>
        )}
        <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
          {state === 'active' && <FooterTextButton onClick={() => setConfirming('pause')} label="Pause" />}
          {state === 'paused' && (
            <FooterTextButton
              onClick={() => {
                void api.resumeHabit(habitId as never).then(onChanged);
              }}
              label="Resume"
            />
          )}
          {state !== 'archived' && <FooterTextButton onClick={() => setConfirming('archive')} label="Archive" />}
        </div>
        <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
          Habits archive rather than delete, so your history stays honest.
        </p>

        {confirming && (
          <LifecycleConfirmDialog
            ariaLabel={confirming === 'pause' ? `Pause ${name}` : `Archive ${name}`}
            message={
              confirming === 'pause'
                ? `Pause "${name}"? It leaves the daily page and the weekly math until you resume. History stays.`
                : `Archive "${name}"? It moves out of active views. Every event stays in your history and your export.`
            }
            confirmLabel={confirming === 'pause' ? 'Pause' : 'Archive'}
            onConfirm={() => {
              const action = confirming === 'pause' ? api.pauseHabit(habitId as never) : api.archiveHabit(habitId as never);
              void action.then(() => {
                setConfirming(null);
                onChanged();
              });
            }}
            onCancel={() => setConfirming(null)}
          />
        )}
      </LifecycleFooterShell>
    </div>
  );
}

function TypeOptionCard({
  icon,
  title,
  description,
  frequency,
  selected,
  disabled,
  onSelect,
}: {
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly description: string;
  readonly frequency: string;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      disabled={disabled}
      onClick={onSelect}
      style={{
        flex: 1,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-lg)',
        border: selected ? '1px solid var(--hue-primary)' : '1px solid var(--border-subtle)',
        background: selected ? 'color-mix(in srgb, var(--hue-primary-vivid) 12%, var(--surface-raised))' : 'var(--surface-raised)',
        textAlign: 'left',
        opacity: disabled && !selected ? 0.5 : 1,
      }}
    >
      <span style={{ color: 'var(--text-secondary)', flex: 'none' }}>{icon}</span>
      <span>
        <span style={{ display: 'block', fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
          {title}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)', marginTop: '2px' }}>
          {description}
        </span>
        <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: '2px' }}>
          {frequency}
        </span>
      </span>
    </button>
  );
}

function FrequencySegment({
  label,
  selected,
  onSelect,
}: {
  readonly label: string;
  readonly selected: boolean;
  readonly onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      style={{
        padding: '0 var(--space-3)',
        minHeight: 'var(--touch-target-min)',
        borderRadius: 'var(--radius-md)',
        border: selected ? '1px solid var(--hue-primary)' : '1px solid var(--border-strong)',
        background: selected ? 'var(--surface-hover)' : 'transparent',
        color: selected ? 'var(--text-primary)' : 'var(--text-secondary)',
        fontWeight: 600,
        fontSize: 'var(--type-body-size)',
      }}
    >
      {label}
    </button>
  );
}

function NumberStepper({
  value,
  min,
  max,
  onChange,
}: {
  readonly value: number;
  readonly min: number;
  readonly max: number;
  readonly onChange: (next: number) => void;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <button
        type="button"
        onClick={() => onChange(Math.max(min, value - 1))}
        aria-label="Decrease"
        disabled={value <= min}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          color: value <= min ? 'var(--text-disabled)' : 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          fontWeight: 600,
        }}
      >
        −
      </button>
      <span className="num" style={{ minWidth: '24px', textAlign: 'center', fontSize: 'var(--type-body-size)', fontWeight: 600, color: 'var(--text-primary)' }}>
        {value}
      </span>
      <button
        type="button"
        onClick={() => onChange(Math.min(max, value + 1))}
        aria-label="Increase"
        disabled={value >= max}
        style={{
          width: 'var(--touch-target-min)',
          height: 'var(--touch-target-min)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          color: value >= max ? 'var(--text-disabled)' : 'var(--text-secondary)',
          fontSize: 'var(--type-body-size)',
          fontWeight: 600,
        }}
      >
        +
      </button>
    </div>
  );
}

function SectionHeader({ children }: { readonly children: string }) {
  return (
    <h2
      style={{
        marginTop: 'var(--space-6)',
        fontSize: 'var(--type-label-size)',
        fontWeight: 'var(--type-label-weight)',
        letterSpacing: 'var(--type-label-tracking)',
        textTransform: 'uppercase',
        color: 'var(--text-muted)',
      }}
    >
      {children}
    </h2>
  );
}

function Breadcrumb({ title, onNavigateBack }: { readonly title: string | null; readonly onNavigateBack?: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', fontSize: 'var(--type-caption-size)', fontWeight: 600 }}>
      {onNavigateBack ? (
        <button type="button" onClick={onNavigateBack} style={{ color: 'var(--text-muted)', minHeight: 'var(--touch-target-min)' }}>
          Habits
        </button>
      ) : (
        <span style={{ color: 'var(--text-muted)' }}>Habits</span>
      )}
      <ChevronRight size={14} color="var(--text-muted)" aria-hidden="true" />
      <span style={{ color: 'var(--text-primary)' }}>{title ? truncate(title, 32) : 'New'}</span>
    </div>
  );
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function TopBar({
  title,
  onNavigateBack,
  onCancel,
  onSubmit,
  submitLabel,
  canSubmit,
}: {
  readonly title: string | null;
  readonly onNavigateBack?: () => void;
  readonly onCancel: () => void;
  readonly onSubmit: () => void;
  readonly submitLabel: string;
  readonly canSubmit: boolean;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
      <Breadcrumb title={title} onNavigateBack={onNavigateBack} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
        <SecondaryButton onClick={onCancel}>Cancel</SecondaryButton>
        <PrimaryButton onClick={onSubmit} disabled={!canSubmit}>
          {submitLabel}
        </PrimaryButton>
      </div>
    </div>
  );
}
